import { Link } from "react-router-dom";
import { Badge, Button, Card } from "@vb/ui";
import { Seo } from "../lib/seo.js";
import { Section, SectionHeading } from "../components/Section.js";

type Status = "live" | "in-dev" | "planned";

interface RoadmapItem {
  title: string;
  status: Status;
  description: string;
  dependencies?: string;
  eta: string;
}

interface RoadmapColumn {
  bucket: string;
  window: string;
  status: "shipping" | "building" | "planned";
  blurb: string;
  items: RoadmapItem[];
}

const COLUMNS: RoadmapColumn[] = [
  {
    bucket: "NOW",
    window: "Q2 2026",
    status: "shipping",
    blurb: "What's deployed today on production. Operators can integrate against everything in this column right now.",
    items: [
      {
        title: "Three live games",
        status: "live",
        description:
          "Vault Breaker (crash, 99% RTP), Pressure Plate (mines-style, 95% RTP), Cipher Crack (puzzle). All three reachable via the same six-endpoint RGS contract.",
        eta: "Shipping",
      },
      {
        title: "Self-serve sandbox",
        status: "live",
        description:
          "POST /public/sandbox/operator mints a 14-day API key with play-money wallet, no card, no contract. The same contract a paying operator gets — only the settlement currency differs.",
        eta: "Shipping",
      },
      {
        title: "Webhook delivery v1",
        status: "live",
        description:
          "Outbound HMAC-signed events: round.opened, round.resolved, session.launched, settlement.recorded. Single delivery attempt with manual replay from the operator console.",
        eta: "Shipping",
      },
      {
        title: "Operator console",
        status: "live",
        description:
          "Read-only dashboard at /operator. GGR by day, settlement history, webhook delivery log, API key rotation. Auth is operator JWT issued at signup.",
        eta: "Shipping",
      },
      {
        title: "Per-operator transparency page",
        status: "live",
        description:
          "Public /transparency/:operatorId with live theoretical vs actual RTP, total wagered, biggest cashout. Operator opts in via a console toggle.",
        eta: "Shipping",
      },
    ],
  },
  {
    bucket: "NEXT",
    window: "Q3 2026",
    status: "building",
    blurb: "Active development. Anything in this column has a working branch and a test harness; ETAs assume the dependencies clear.",
    items: [
      {
        title: "GLI-19 audit kickoff",
        status: "in-dev",
        description:
          "Engagement letter signed with GLI (or iTechLabs as backup). Independent third-party RNG and math review. We commit to commencing within 60 days of first signed pilot.",
        dependencies: "Depends on first paying operator funding the audit (~$25-40k).",
        eta: "Aug 2026",
      },
      {
        title: "Signed launch tokens",
        status: "in-dev",
        description:
          "Operator HMAC-signs the iframe launch URL (5-min TTL JWT). We verify before minting the session. Replaces the current plaintext operatorId convention. Backwards-compatible behind a feature flag for one quarter.",
        dependencies: "Operator adapter SDK update — ~30 lines of TypeScript per operator.",
        eta: "Jul 2026",
      },
      {
        title: "On-chain settlement-proof commits",
        status: "in-dev",
        description:
          "Weekly settlement TX hash + GGR digest anchored to Solana via a Memo program write. Anyone can re-derive the digest from the disclosed round seeds and confirm we paid what we owed, with a block-timestamp guarantee.",
        dependencies: "Solana wallet provisioning + ~$5/mo SOL for memo fees per operator.",
        eta: "Sep 2026",
      },
      {
        title: "Cold Deck (table game)",
        status: "in-dev",
        description:
          "Fourth title — provably-fair card game in the Vault Breaker visual language. Shoe-shuffle commit-reveal. Same RGS contract; new ws event vocabulary (deal, hold, reveal).",
        dependencies: "Engine work is done; art and round-flow QA outstanding.",
        eta: "Sep 2026",
      },
      {
        title: "Webhook delivery v2",
        status: "in-dev",
        description:
          "Exponential backoff (1m → 5m → 25m → 2h → 10h, 5 attempts), dead-letter queue with operator-console replay UI, per-event delivery SLO dashboard. Replaces the v1 single-shot worker.",
        eta: "Aug 2026",
      },
    ],
  },
  {
    bucket: "LATER",
    window: "Q4 2026 – Q1 2027",
    status: "planned",
    blurb: "Committed direction, no working branch yet. Sequencing is driven by the first cohort of paying operators — happy to negotiate priority during pilot.",
    items: [
      {
        title: "Multi-tenant DB + horizontal scaling",
        status: "planned",
        description:
          "Today the game-server is a single Fly LHR instance backed by a single Postgres. Sharding by operatorId, read-replicas in EWR/NRT, and a connection-pooler tier. Required before we cross ~5k concurrent rounds.",
        dependencies: "Triggered by sustained traffic from the first three operators.",
        eta: "Q4 2026",
      },
      {
        title: "Aggregator adapters",
        status: "planned",
        description:
          "First-party adapters for SoftSwiss and Relax for operators that prefer the aggregator-side integration. We lose the direct relationship, but operators on those stacks integrate in a day instead of a week.",
        dependencies: "Aggregator partnership program enrollment (3-6 month process each).",
        eta: "Q4 2026 – Q1 2027",
      },
      {
        title: "Live multi-language support",
        status: "planned",
        description:
          "Spanish, Portuguese, Russian, Korean — reflecting the top crypto-casino markets by traffic. Strings only; no per-locale RTP or jurisdiction logic. Loaded on demand to keep the iframe payload small.",
        eta: "Q1 2027",
      },
      {
        title: "Two more games — Split Six, Red Door",
        status: "planned",
        description:
          "Split Six is a dice variant; Red Door is a slot-adjacent reveal game with the same provably-fair scheme. Both carry the Vault Breaker visual identity. Brings the catalogue to six.",
        eta: "Q1 2027",
      },
    ],
  },
];

const PAGE_LD = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "Roadmap · Vault Breaker Studios",
  description:
    "Twelve-month delivery roadmap for Vault Breaker Studios — what's shipping, what's building, and what's planned, with honest dates and dependencies.",
  url: "https://vaultbreaker.dev/roadmap",
  publisher: {
    "@type": "Organization",
    name: "Vault Breaker Studios",
    url: "https://vaultbreaker.dev",
  },
} as const;

export function Roadmap() {
  return (
    <>
      <Seo
        title="Roadmap · Vault Breaker Studios"
        description="What Vault Breaker Studios ships in the next 12 months — honest dates, honest dependencies, no marketing fog."
        path="/roadmap"
        jsonLd={PAGE_LD}
      />

      <Section style={{ paddingBottom: 32 }}>
        <Badge kind="fair" style={{ marginBottom: 18 }}>
          PUBLIC ROADMAP
        </Badge>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "clamp(44px, 6vw, 72px)",
            lineHeight: 1,
            margin: 0,
            color: "var(--fg-1)",
            textTransform: "uppercase",
          }}
        >
          Roadmap
        </h1>
        <p
          style={{
            fontSize: 17,
            color: "var(--fg-2)",
            marginTop: 14,
            maxWidth: 720,
            lineHeight: 1.55,
          }}
        >
          What we ship in the next 12 months. Honest dates, honest dependencies. We update this page
          when items move columns — not when marketing wants a milestone.
        </p>
      </Section>

      <Section>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: 18,
            alignItems: "stretch",
          }}
        >
          {COLUMNS.map((col) => (
            <RoadmapColumnCard key={col.bucket} column={col} />
          ))}
        </div>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="Status legend"
          title="Three colors, three meanings"
          lead="No 'coming soon' or 'in beta' weasel words. A line is either deployed, on a working branch, or a written commitment."
        />
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <LegendRow
            color="var(--green)"
            label="LIVE"
            blurb="Deployed to production. Reachable from every operator API key today."
            first
          />
          <LegendRow
            color="var(--cyan)"
            label="IN-DEV"
            blurb="Active branch with tests. ETA assumes the listed dependencies clear; we update the date on this page if they slip."
          />
          <LegendRow
            color="var(--gold-bright)"
            label="PLANNED"
            blurb="Committed direction. No working branch yet — pilot operators can negotiate sequence."
          />
        </Card>
      </Section>

      <Section>
        <Card
          style={{
            padding: 22,
            display: "grid",
            gap: 12,
            background: "var(--bg-2)",
          }}
        >
          <h2
            style={{
              margin: 0,
              fontFamily: "var(--font-display)",
              fontSize: 22,
              color: "var(--fg-1)",
              textTransform: "uppercase",
              letterSpacing: "0.01em",
            }}
          >
            Need something not on this list?
          </h2>
          <p style={{ margin: 0, fontSize: 14, color: "var(--fg-2)", lineHeight: 1.6 }}>
            Operators can request a custom roadmap discussion via{" "}
            <Link to="/contact" style={{ color: "var(--cyan)" }}>
              /contact
            </Link>
            . If it's a one-off integration we'll quote it. If it's a feature several operators need,
            we'll tell you which column it belongs in and when it lands.
          </p>
        </Card>
      </Section>

      <Section>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <Link to="/contact" style={{ textDecoration: "none" }}>
            <Button variant="gold" style={{ padding: "14px 22px", fontSize: 15 }}>
              CONTACT SALES
            </Button>
          </Link>
          <Link to="/sandbox" style={{ textDecoration: "none" }}>
            <Button style={{ padding: "14px 22px", fontSize: 15 }}>OPEN A SANDBOX</Button>
          </Link>
        </div>
      </Section>
    </>
  );
}

function RoadmapColumnCard({ column }: { column: RoadmapColumn }) {
  return (
    <Card
      style={{
        padding: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "18px 22px",
          borderBottom: "1px solid var(--line)",
          display: "grid",
          gap: 6,
        }}
      >
        <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
          <span
            style={{
              fontFamily: "var(--font-display)",
              fontSize: 28,
              color: "var(--fg-1)",
              letterSpacing: "0.02em",
            }}
          >
            {column.bucket}
          </span>
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 12,
              color: "var(--fg-3)",
            }}
          >
            {column.window}
          </span>
        </div>
        <span
          className="label"
          style={{
            fontSize: 10,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color:
              column.status === "shipping"
                ? "var(--green)"
                : column.status === "building"
                  ? "var(--cyan)"
                  : "var(--gold-bright)",
          }}
        >
          Status · {column.status}
        </span>
        <p style={{ margin: "4px 0 0 0", fontSize: 12, color: "var(--fg-2)", lineHeight: 1.55 }}>
          {column.blurb}
        </p>
      </div>
      <div style={{ display: "grid" }}>
        {column.items.map((item, i) => (
          <RoadmapItemRow key={item.title} item={item} first={i === 0} />
        ))}
      </div>
    </Card>
  );
}

function RoadmapItemRow({ item, first }: { item: RoadmapItem; first: boolean }) {
  const color =
    item.status === "live"
      ? "var(--green)"
      : item.status === "in-dev"
        ? "var(--cyan)"
        : "var(--gold-bright)";
  const label = item.status === "live" ? "LIVE" : item.status === "in-dev" ? "IN-DEV" : "PLANNED";
  return (
    <div
      style={{
        padding: "16px 22px",
        borderTop: first ? "none" : "1px solid var(--line)",
        display: "grid",
        gap: 8,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            padding: "3px 7px",
            borderRadius: 4,
            background: "rgba(255,255,255,0.04)",
            color,
            border: `1px solid ${color}`,
            letterSpacing: "0.08em",
          }}
        >
          {label}
        </span>
        <h3 style={{ margin: 0, fontSize: 14, color: "var(--fg-1)" }}>{item.title}</h3>
      </div>
      <p style={{ margin: 0, fontSize: 13, color: "var(--fg-2)", lineHeight: 1.6 }}>
        {item.description}
      </p>
      {item.dependencies && (
        <p
          style={{
            margin: 0,
            fontSize: 12,
            color: "var(--fg-3)",
            lineHeight: 1.55,
            fontFamily: "var(--font-mono)",
          }}
        >
          dep · {item.dependencies}
        </p>
      )}
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 11,
          color: "var(--fg-3)",
          letterSpacing: "0.02em",
        }}
      >
        ETA · {item.eta}
      </span>
    </div>
  );
}

function LegendRow({
  color,
  label,
  blurb,
  first,
}: {
  color: string;
  label: string;
  blurb: string;
  first?: boolean;
}) {
  return (
    <div
      style={{
        padding: "16px 22px",
        borderTop: first ? "none" : "1px solid var(--line)",
        display: "grid",
        gridTemplateColumns: "120px 1fr",
        gap: 16,
        alignItems: "center",
      }}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          fontSize: 11,
          letterSpacing: "0.1em",
          color,
          fontFamily: "var(--font-mono)",
        }}
      >
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: 999,
            background: color,
            flexShrink: 0,
          }}
        />
        {label}
      </span>
      <span style={{ fontSize: 13, color: "var(--fg-2)", lineHeight: 1.55 }}>{blurb}</span>
    </div>
  );
}
